import React from 'react'
import Notification from './notification'
import Message from './message'
import Stack from './stack'
import Modal from './modal'
import Toast from './toast'
import { AnimatePresence, motion } from 'framer-motion'

export interface IToast {
  title: string
  id: string
  duration?: number
  autoClose?: boolean
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'error'
}

export interface IMessage {
  title: string
  id: string
  duration?: number
  variant?: 'default' | 'primary' | 'success' | 'warning'
}

export interface INotification {
  title: string
  description: string
  id: string
  autoClose?: boolean
  duration?: number
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'error'
}

export interface IModal {
  title: string
  content: React.ReactNode | string
  id: string
}

export const ToastContext = React.createContext<{
  toasts: IToast[]
  pushToast: (toast: IToast) => void
  deleteToast: (id: string) => void
}>({
  toasts: [],
  pushToast: () => undefined,
  deleteToast: () => undefined
})

export const ModalContext = React.createContext<{
  modals: IModal[]
  pushModal: (modal: IModal) => void
  deleteModal: (id: string) => void
}>({
  modals: [],
  pushModal: () => undefined,
  deleteModal: () => undefined
})

export const NotificationContext = React.createContext<{
  notifications: INotification[]
  pushNotification: (notification: INotification) => void
  deleteNotification: (id: string) => void
}>({
  notifications: [],
  pushNotification: () => undefined,
  deleteNotification: () => undefined
})

export const MessageContext = React.createContext<{
  messages: IMessage[]
  pushMessage: (message: IMessage) => void
  deleteMessage: (id: string) => void
}>({
  messages: [],
  pushMessage: () => undefined,
  deleteMessage: () => undefined
})

const Container: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = React.useState<IToast[]>([])
  const [notifications, setNotifications] = React.useState<INotification[]>([])
  const [modalClosing, setModalClosing] = React.useState<string>()
  const [messages, setMessages] = React.useState<IMessage[]>([])
  const [modals, setModals] = React.useState<IModal[]>([])

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        pushNotification: (notification) =>
          setNotifications([...notifications, notification]),
        deleteNotification: (id) =>
          setNotifications(notifications.filter((n) => n.id !== id))
      }}
    >
      <MessageContext.Provider
        value={{
          messages,
          pushMessage: (message: IMessage) =>
            setMessages([...messages, message]),
          deleteMessage: (id) =>
            setMessages(messages.filter((n) => n.id !== id))
        }}
      >
        <ModalContext.Provider
          value={{
            modals,
            pushModal: (modal: IModal) => setModals([...modals, modal]),
            deleteModal: (id: string) =>
              setModals(modals.filter((m) => m.id !== id))
          }}
        >
          <ToastContext.Provider
            value={{
              toasts,
              pushToast: (toast: IToast) => setToasts([...toasts, toast]),
              deleteToast: (id: string) =>
                setToasts(toasts.filter((t) => t.id !== id))
            }}
          >
            <Stack direction='column' className='toasts'>
              <AnimatePresence>
                {toasts.map((toast) => (
                  <motion.div
                    key={toast.id}
                    layout
                    transition={{ ease: 'easeOut' }}
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -50 }}
                  >
                    <Toast {...toast} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </Stack>

            <Stack direction='column' className='notifications'>
              <AnimatePresence>
                {notifications.map((n) => (
                  <motion.div
                    key={n.id}
                    layout
                    transition={{ ease: 'easeOut' }}
                    initial={{ opacity: 0, x: 350 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 350 }}
                  >
                    <Notification {...n} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </Stack>

            <Stack direction='column' className='messages' align='center'>
              <AnimatePresence>
                {messages.map((n) => (
                  <motion.div
                    key={n.id}
                    layout
                    transition={{ ease: 'easeOut' }}
                    initial={{ opacity: 0, y: -50 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -50 }}
                  >
                    <Message {...n} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </Stack>

            {modals.map((modal) => (
              <Modal
                onClose={() => {
                  setModalClosing(modal.id)
                  setTimeout(() => {
                    setModals(modals.filter((m) => m.id !== modal.id))
                    setModalClosing(undefined)
                  }, 1000)
                }}
                title={modal.title}
                isVisible={
                  modals.some((m) => m.id === modal.id) &&
                  modalClosing !== modal.id
                }
                key={modal.id}
              >
                {modal.content}
              </Modal>
            ))}

            {children}
          </ToastContext.Provider>
        </ModalContext.Provider>
      </MessageContext.Provider>
    </NotificationContext.Provider>
  )
}

export default Container
